import { useEffect, useState } from 'react'
import type { ProductV2 } from '../products-v2/types'
import { gingerbreadCatalogAPI } from './api'
import type { GingerbreadBlock, GingerbreadRow } from './types'

/** Префиксы категорий пряников: PP0 — пряник, PP1 — набор, PP2 — набор из кругов «Капк 5». */
const GINGERBREAD_PREFIXES = ['PP0', 'PP1', 'PP2']

/**
 * Товар относится к пряникам: блок генератора уже в строке, либо категория
 * PP0/PP1/PP2, либо (если категории в данных нет) артикул вида PP0…/PP1…/PP2….
 */
export function isGingerbreadProduct(product: ProductV2 | null | undefined): boolean {
  if (!product) return false
  if ((product as GingerbreadRow).gingerbread) return true
  const prefix = String(product.category?.prefix ?? '').trim().toUpperCase()
  if (prefix) return GINGERBREAD_PREFIXES.includes(prefix)
  return /^PP[012]\d/i.test(String(product.article ?? ''))
}

/** Артикул пряничной категории по одному имени, пока товар ещё не загружен. */
export function isGingerbreadArticle(article: string | null | undefined): boolean {
  return /^PP[012]\d/i.test(String(article ?? ''))
}

/**
 * Вид пряничного товара до загрузки блока генератора: по артикулу и категории.
 * После загрузки точнее блок (`kind`, `origin`), но шапка не должна ждать.
 */
export function provisionalKind(product: ProductV2): 'single' | 'kit' | 'kit_pp2' {
  const block = (product as GingerbreadRow).gingerbread
  if (block) return block.kind === 'single' ? 'single' : block.origin === 'kapk5' ? 'kit_pp2' : 'kit'
  const prefix = String(product.category?.prefix ?? '').trim().toUpperCase()
  const key = GINGERBREAD_PREFIXES.includes(prefix) ? prefix : String(product.article ?? '').slice(0, 3).toUpperCase()
  if (key === 'PP2') return 'kit_pp2'
  if (key === 'PP1') return 'kit'
  return 'single'
}

/**
 * Блок производственных данных пряника. Он есть в строке каталога пряников, но
 * когда окно открыли по ссылке `?article=` или из другого окна, строки нет:
 * догружаем ту же строку каталога. `kind=all` наборы PP2 не включает — у них
 * своя вкладка, поэтому PP2 ищем в ней.
 *
 * `undefined` — ещё грузится, `null` — данных генератора у товара нет.
 *
 * `articleHint` — артикул окна, известный до загрузки товара: запрос блока идёт
 * параллельно запросам товара, а не после них (товар в окне ждёт 3–5 секунд, пока
 * страница-хозяин гонит свои запросы, и блок раньше стартовал только потом).
 */
export function useGingerbreadBlock(
  product: ProductV2 | null | undefined,
  enabled = true,
  articleHint?: string | null,
): { block: GingerbreadBlock | null | undefined; loading: boolean } {
  const article = product?.article ?? articleHint ?? undefined
  const fromRow = (product as GingerbreadRow | null | undefined)?.gingerbread
  const [block, setBlock] = useState<GingerbreadBlock | null | undefined>(fromRow)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setBlock(fromRow)
    if (!enabled || !article || fromRow !== undefined) {
      setLoading(false)
      return undefined
    }
    let cancelled = false
    setLoading(true)
    const kind = /^PP2/i.test(article) ? 'kit_pp2' : 'all'
    gingerbreadCatalogAPI
      .getCatalog({ articles: article, kind, limit: 1, offset: 0 })
      .then((page) => {
        if (!cancelled) setBlock(page.products.find((row) => row.article === article)?.gingerbread ?? null)
      })
      .catch(() => {
        if (!cancelled) setBlock(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [article, fromRow, enabled])

  return { block, loading }
}
